import React, { useEffect, FC } from 'react'
import { Box } from '../../../../legacy-design-system/form'
import { useIntl } from 'react-intl'
import { useJiraIntegrationPage } from './jira-integration-page.provider'
import { css } from '@emotion/css'
import { Label } from '../../../../legacy-design-system/form-label'
import { theme as deprecatedTheme } from '../../../../styled'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { usePrioritiesQuery } from '../api/use-priorities-query'
import { DefaultPriorityBadge } from './default-priority-badge'
import { useController, useFormContext } from 'react-hook-form'
import { transformFormDataToBugtrackerRequest } from '../../bugtrackers/api/build-api-payload-bugtracker-credentials'
import { useProjectFieldsQuery } from '../api/use-project-fields-query'
import { JiraFormFieldTypes } from './jira-form-field-types'
import { JiraIntegrationForm } from './jira-form-schema'

const prioritiesSectionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const prioritiesTitleStyles = css`
  display: flex;
  flex-direction: row;
  gap: 4px;
`

export const BASE_PRIORITY_TEST_ID = 'priority_test_id'

export const clearDefaultPriorityValues = (setValue) => {
  const priorityFields = [
    JiraFormFieldTypes.PRIORITY_CRITICAL,
    JiraFormFieldTypes.PRIORITY_HIGH,
    JiraFormFieldTypes.PRIORITY_MEDIUM,
    JiraFormFieldTypes.PRIORITY_LOW,
    JiraFormFieldTypes.PRIORITY_NOTE
  ]
  priorityFields.forEach((priorityField) => {
    setValue(priorityField, null)
  })
}

export const setDefaultPriorityValues = (setValue, priority?: string) => {
  // need to default priorities if were previously editing a non-severity project
  setValue(JiraFormFieldTypes.PRIORITY_CRITICAL, priority ?? 'Critical')
  setValue(JiraFormFieldTypes.PRIORITY_HIGH, priority ?? 'Critical')
  setValue(JiraFormFieldTypes.PRIORITY_MEDIUM, priority ?? 'Major')
  setValue(JiraFormFieldTypes.PRIORITY_LOW, priority ?? 'Standard')
  setValue(JiraFormFieldTypes.PRIORITY_NOTE, priority ?? 'Standard')
}
export const DefaultPriority: FC = () => {
  const {
    state: { organizationUuid, bugtrackerId }
  } = useJiraIntegrationPage()
  const { getValues, watch, setValue } = useFormContext<JiraIntegrationForm>()
  const { formatMessage } = useIntl()
  const watchProject = watch(JiraFormFieldTypes.PROJECT)
  const watchIssueType = watch(JiraFormFieldTypes.ISSUE_TYPE)
  const { priorities } = usePrioritiesQuery({
    organizationUuid,
    bugtrackerRequest: transformFormDataToBugtrackerRequest(getValues),
    projectName:
      watchProject && typeof watchProject === 'object' ? watchProject.key : ''
  })

  const projectKey =
    watchProject && typeof watchProject === 'object'
      ? watchProject.key
      : undefined
  const issueTypeId =
    watchIssueType && typeof watchIssueType === 'object'
      ? watchIssueType.id
      : undefined

  const { data } = useProjectFieldsQuery({
    organizationUuid,
    bugtrackerRequest: transformFormDataToBugtrackerRequest(getValues),
    projectId: projectKey,
    issueTypeId
  })

  const enabled = data ? data.priorityFieldAvailable : true
  const {
    field: {
      onChange: onPriorityFieldAvailableChange,
      value: priorityFieldAvailableValue
    }
  } = useController({ name: JiraFormFieldTypes.PRIORITY_FIELD_AVAILABLE })

  useEffect(() => {
    if (data && data.priorityFieldAvailable !== priorityFieldAvailableValue) {
      onPriorityFieldAvailableChange(data.priorityFieldAvailable)
      if (!data.priorityFieldAvailable) {
        clearDefaultPriorityValues(setValue)
      }
      if (
        // this means we have switched from a non-priority project
        data.priorityFieldAvailable &&
        !getValues(JiraFormFieldTypes.PRIORITY_CRITICAL)
      ) {
        setDefaultPriorityValues(setValue)
      }
    }

    if (priorities && !bugtrackerId) {
      setDefaultPriorityValues(setValue, priorities[0].name)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, priorityFieldAvailableValue])

  return (
    <>
      {watchProject && watchIssueType && (
        <Box basis={3}>
          <div className={prioritiesSectionStyles}>
            <div className={prioritiesTitleStyles}>
              <Label>
                {formatMessage({
                  id: 'title_integrationsSettings_priorityDefault'
                })}
              </Label>
              <Label
                className={css`
                  color: ${theme.color.neutral60};
                  font-weight: ${deprecatedTheme.fontWeights.primary.regular};
                `}
              >
                {formatMessage({
                  id: 'detail_integrationsSettings_priorityBasedOnSeverity'
                })}
              </Label>
            </div>

            <DefaultPriorityBadge
              name={JiraFormFieldTypes.PRIORITY_CRITICAL}
              color="red50"
              label="title_vulnSeverityTypeCritical"
              priorities={priorities}
              inputDataTestId={`${BASE_PRIORITY_TEST_ID}-${JiraFormFieldTypes.PRIORITY_CRITICAL}`}
              disabled={!enabled}
            />

            <DefaultPriorityBadge
              name={JiraFormFieldTypes.PRIORITY_HIGH}
              color="orange50"
              label="title_vulnSeverityTypeHigh"
              priorities={priorities}
              inputDataTestId={`${BASE_PRIORITY_TEST_ID}-${JiraFormFieldTypes.PRIORITY_HIGH}`}
              disabled={!enabled}
            />

            <DefaultPriorityBadge
              name={JiraFormFieldTypes.PRIORITY_MEDIUM}
              color="yellow50"
              label="title_vulnSeverityTypeMedium"
              priorities={priorities}
              inputDataTestId={`${BASE_PRIORITY_TEST_ID}-${JiraFormFieldTypes.PRIORITY_MEDIUM}`}
              disabled={!enabled}
            />

            <DefaultPriorityBadge
              name={JiraFormFieldTypes.PRIORITY_LOW}
              color="neutral40"
              label="title_vulnSeverityTypeLow"
              priorities={priorities}
              inputDataTestId={`${BASE_PRIORITY_TEST_ID}-${JiraFormFieldTypes.PRIORITY_LOW}`}
              disabled={!enabled}
            />

            <DefaultPriorityBadge
              name={JiraFormFieldTypes.PRIORITY_NOTE}
              color="neutral40"
              label="title_vulnSeverityTypeNote"
              priorities={priorities}
              inputDataTestId={`${BASE_PRIORITY_TEST_ID}-${JiraFormFieldTypes.PRIORITY_NOTE}`}
              disabled={!enabled}
            />
          </div>
        </Box>
      )}
    </>
  )
}
